import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getHeaders } from '../utils/headers.functions';

const API_URL = environment.API_URL;

export interface SubscriptionPlan {
  id: number;
  name: string;
  description?: string;
  amount: number;
  currency: string;
  repeat_unit: 'month' | 'year';
  trial_days: number;
  max_seats: number;
  features: string[];
  status: 'active' | 'inactive';
}

export interface CustomerSubscription {
  id: number;
  user_id: number;
  plan_id: number;
  status: 'trial' | 'active' | 'past_due' | 'paused' | 'cancelled';
  trial_end_date?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancelled_at?: string;
  cancel_reason?: string;
  seats_used: number;
  Plan?: SubscriptionPlan;
  created_at?: string;
}

export interface RecurringPayment {
  id: number;
  subscription_id: number;
  user_id: number;
  amount: number;
  currency: string;
  status: 'completed' | 'failed' | 'pending' | 'refunded';
  attempt_number: number;
  error_message?: string;
  paid_at?: string;
  period_start?: string;
  period_end?: string;
  cfdi_url?: string;
  created_at?: string;
}

export interface PaginatedPayments {
  count: number;
  numPages: number;
  rows: RecurringPayment[];
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  constructor(private http: HttpClient) {}

  getMySubscription(userId: number): Observable<{ subscription: CustomerSubscription | null }> {
    const headers = getHeaders();
    return this.http.get<{ subscription: CustomerSubscription | null }>(
      `${API_URL}/my-subscription/${userId}`,
      { headers },
    );
  }

  getPaymentHistory(userId: number, page = 0, limit = 10): Observable<PaginatedPayments> {
    const headers = getHeaders();
    return this.http.get<PaginatedPayments>(
      `${API_URL}/subscription-payment-history/${userId}?limit=${limit}&offset=${page}`,
      { headers },
    );
  }

  getPlans(): Observable<SubscriptionPlan[]> {
    return this.http.get<SubscriptionPlan[]>(`${API_URL}/subscription-plans`);
  }

  cancelSubscription(subscriptionId: number, reason?: string): Observable<{ message: string }> {
    const headers = getHeaders();
    return this.http.put<{ message: string }>(
      `${API_URL}/customer-subscriptions/${subscriptionId}/cancel`,
      { reason },
      { headers },
    );
  }

  upgradePlan(subscriptionId: number, newPlanId: number): Observable<{ message: string }> {
    const headers = getHeaders();
    return this.http.put<{ message: string }>(
      `${API_URL}/customer-subscriptions/${subscriptionId}/plan`,
      { newPlanId },
      { headers },
    );
  }
}
